import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { MINUTE_MS } from 'data/constants'
import { formatUnitsToFixedLength, parseUnits } from 'hooks/useBalance'
import { queryClient } from 'use-towns-client'

import { fetchEthPrice } from './fetchEthPrice'

const queryKey = () => ['ethPrice']
const fallbackQueryKey = () => ['ethPrice', 'fallback']

const DEFAULT_REFETCH_INTERVAL = 10 * MINUTE_MS

export function useEthPrice(
    args: {
        enabled?: boolean
        refetchInterval?: number
    } = {},
) {
    const { enabled = true, refetchInterval = DEFAULT_REFETCH_INTERVAL } = args
    const queryClient = useQueryClient()
    const _queryKey = queryKey()
    const _fallbackQueryKey = fallbackQueryKey()

    const globalFallbackMode = queryClient.getQueryData<boolean>(_fallbackQueryKey) ?? false

    const { data, isLoading, isError, ...rest } = useQuery({
        queryKey: _queryKey,
        queryFn: fetchEthPrice,
        enabled: enabled && !globalFallbackMode,
        refetchInterval,
        staleTime: DEFAULT_REFETCH_INTERVAL,
        gcTime: DEFAULT_REFETCH_INTERVAL,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    })

    useEffect(() => {
        if (isError || (!isLoading && data === '0')) {
            queryClient.setQueryData(_fallbackQueryKey, true)
        } else if (data && data !== '0') {
            queryClient.setQueryData(_fallbackQueryKey, false)
        }
    }, [isError, isLoading, data, queryClient, _fallbackQueryKey])

    return { data, isLoading, isError, ...rest, isFallbackMode: globalFallbackMode }
}

export function ensureEthPrice() {
    return queryClient.ensureQueryData({
        queryKey: queryKey(),
        queryFn: fetchEthPrice,
        staleTime: DEFAULT_REFETCH_INTERVAL,
        gcTime: DEFAULT_REFETCH_INTERVAL,
    })
}

export function calculateEthAmountFromUsd(args: { cents: number; ethPriceInUsd: string }) {
    const { cents, ethPriceInUsd } = args
    const usdAmount = cents / 100
    const usdAmountBigInt = parseUnits(usdAmount.toString(), 18)
    const ethPriceInUsdBigInt = parseUnits(ethPriceInUsd, 18)
    const ethAmountBigInt = (usdAmountBigInt * BigInt(1e18)) / ethPriceInUsdBigInt

    return {
        value: ethAmountBigInt,
        formatted: formatUnitsToFixedLength(ethAmountBigInt),
    }
}

export function calculateUsdcAmountFromCents(cents: number) {
    const usdAmount = cents / 100
    // USDC has 6 decimals, assume 1 USDC = $1
    const usdcAmountBigInt = parseUnits(usdAmount.toString(), 6)

    return {
        value: usdcAmountBigInt,
        formatted: formatUnitsToFixedLength(usdcAmountBigInt, 6, 2), // 2 decimals for display
    }
}

export const INVALID_CALCULATION = 'INVALID'
export function calculateUsdAmountFromToken(args: {
    tokenAmount: unknown | undefined
    tokenPriceInUsd: unknown | undefined
    decimals?: number
}): bigint | typeof INVALID_CALCULATION | undefined {
    const { tokenAmount, tokenPriceInUsd, decimals = 18 } = args
    if (!tokenAmount || !tokenPriceInUsd) {
        return undefined
    }

    let amountAsBigInt: bigint
    try {
        // @ts-expect-error - unknown type
        amountAsBigInt = BigInt(tokenAmount)
    } catch (error) {
        console.warn(
            '[calculateUsdAmountFromToken] Failed to convert tokenAmount to BigInt:',
            tokenAmount,
            error,
        )
        return INVALID_CALCULATION
    }
    if (typeof tokenPriceInUsd !== 'string') {
        console.warn(
            '[calculateUsdAmountFromToken] tokenPriceInUsd is not a string',
            tokenPriceInUsd,
        )
        return INVALID_CALCULATION
    }
    if (!/^\d+(\.\d+)?$/.test(tokenPriceInUsd)) {
        console.warn(
            '[calculateUsdAmountFromToken] tokenPriceInUsd is not a valid numerical string:',
            tokenPriceInUsd,
        )
        return undefined
    }

    const tokenPriceInUsdBigInt = parseUnits(tokenPriceInUsd, decimals)
    const usdValueBigInt = (amountAsBigInt * tokenPriceInUsdBigInt) / 10n ** BigInt(decimals)
    return usdValueBigInt
}

export function useEthToUsdFormatted(args: {
    ethAmount: bigint | undefined
    refetchInterval?: number
    noDecimals?: boolean
}) {
    const { ethAmount, refetchInterval = 8_000, noDecimals = false } = args
    const queryClient = useQueryClient()
    const _fallbackQueryKey = fallbackQueryKey()

    const globalFallbackMode = queryClient.getQueryData<boolean>(_fallbackQueryKey) ?? false

    const { data: ethPrice } = useEthPrice({
        enabled: !globalFallbackMode,
        refetchInterval: refetchInterval,
    })

    if (globalFallbackMode) {
        return '--'
    }

    const amount = calculateUsdAmountFromToken({
        tokenAmount: ethAmount,
        tokenPriceInUsd: ethPrice?.toString(),
    })
    if (amount === INVALID_CALCULATION) {
        return '--'
    }
    return formatUsd(formatUnitsToFixedLength(amount || 0n), {
        noDecimals,
    })
}

/**
 * same as useEthToUsdFormatted, but returns an imperative getter function
 * instead of a value
 */
export const useEthToUsdFormatter = () => {
    const queryClient = useQueryClient()
    const _fallbackQueryKey = fallbackQueryKey()
    const globalFallbackMode = queryClient.getQueryData<boolean>(_fallbackQueryKey) ?? false
    const { data: ethPrice } = useEthPrice({
        enabled: !globalFallbackMode,
        refetchInterval: MINUTE_MS,
    })

    return useCallback(
        (ethAmount: bigint, opts?: { noDecimals?: boolean }) => {
            const globalFallbackMode = queryClient.getQueryData<boolean>(_fallbackQueryKey) ?? false

            if (globalFallbackMode) {
                return '--'
            }

            if (!ethPrice) {
                return '--'
            }

            const amount = calculateUsdAmountFromToken({
                tokenAmount: ethAmount,
                tokenPriceInUsd: ethPrice,
            })

            if (amount === INVALID_CALCULATION) {
                return '--'
            }

            return formatUsd(formatUnitsToFixedLength(amount || 0n), opts)
        },
        [queryClient, _fallbackQueryKey, ethPrice],
    )
}

/**
 * Imperative function to format USD value from ETH amount
 * This function fetches the current ETH price and calculates the USD equivalent
 */
export async function formatUsdFromEth(
    ethAmount: bigint,
    opts?: { noDecimals?: boolean },
): Promise<string> {
    try {
        const ethPrice = await ensureEthPrice()

        const usdAmount = calculateUsdAmountFromToken({
            tokenAmount: ethAmount,
            tokenPriceInUsd: ethPrice,
        })

        if (usdAmount === INVALID_CALCULATION || usdAmount === undefined) {
            return '--'
        }

        return formatUsd(formatUnitsToFixedLength(usdAmount), opts)
    } catch (error) {
        console.warn('[formatUsdFromEth] Failed to format USD from ETH:', error)
        return '--'
    }
}

/**
 * Synchronous imperative function to format USD from ETH using cached price
 * Returns '--' if no cached price is available
 */
export function formatUsdFromEthSync(ethAmount: bigint, opts?: { noDecimals?: boolean }): string {
    const _fallbackQueryKey = fallbackQueryKey()
    const globalFallbackMode = queryClient.getQueryData<boolean>(_fallbackQueryKey) ?? false

    if (globalFallbackMode) {
        return '--'
    }

    const ethPrice = queryClient.getQueryData<string>(queryKey())

    if (!ethPrice) {
        return '--'
    }

    const usdAmount = calculateUsdAmountFromToken({
        tokenAmount: ethAmount,
        tokenPriceInUsd: ethPrice,
    })

    if (usdAmount === INVALID_CALCULATION || usdAmount === undefined) {
        return '--'
    }

    // If USD value is effectively zero, show $0.00
    if (usdAmount < 10000000000n) {
        // less than $0.00000001 (with 18 decimals)
        return '$0.00'
    }

    return formatUsd(formatUnitsToFixedLength(usdAmount), opts)
}

export const formatUsd = (totalPrice: string, opts?: { noDecimals?: boolean }) => {
    const parsed = parseFloat(totalPrice)
    const useNoDecimals = opts?.noDecimals && parsed >= 1
    return `$${Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: useNoDecimals ? 0 : 2,
        maximumFractionDigits: useNoDecimals ? 0 : 2,
    }).format(parsed)}`
}
